import React, { useEffect, useRef, useState } from 'react';

import Api from 'api';
import ApiError from 'api/ApiError';
import FixedError from 'components/FixedError';
import {
  AssistFeedback,
  CoachInterrupt,
  HardStop,
  RedactionPreview,
} from 'components/WritingAssistMoments';
import type {
  BlockingCategory,
  ContactViolationCategory,
} from 'components/WritingAssistMoments/WritingAssistMoments.types';

import WritingAssist from './WritingAssist';
import type {
  WritingAssistProps,
  WritingAssistResponse,
  WritingAssistSaveSelection,
  WritingAssistSuccessResponse,
} from './WritingAssist.types';
import type {
  WritingAssistGateEnvelope,
  WritingAssistSavedResult,
} from './WritingAssistFlow.types';
import {
  isWritingAssistGateUnavailableError,
  writingAssistGateEnvelopeFromError,
} from './writingAssistGate';

type BaseProps = Omit<
  WritingAssistProps,
  'onSave' | 'onWriterResult' | 'onGateResult' | 'onSaveError'
>;

export type WritingAssistFlowProps = BaseProps & {
  save: (selection: WritingAssistSaveSelection) => Promise<unknown> | unknown;
  onSaved?: (saved: WritingAssistSavedResult) => void;
  onSaveError?: (error: unknown) => void;
  intervention?: WritingAssistGateEnvelope | null;
  interventionSelection?: WritingAssistSaveSelection | null;
  onInterventionChange?: (intervention: WritingAssistGateEnvelope | null) => void;
};

const BLOCKING_CATEGORIES: readonly BlockingCategory[] = [
  'offsite_steering', 'competitor', 'disparagement', 'toxic',
];
const CONTACT_CATEGORIES: readonly ContactViolationCategory[] = [
  'contact_phone', 'contact_email', 'contact_url', 'contact_social',
];

const WritingAssistFlow: React.FC<WritingAssistFlowProps> = ({
  save,
  onSaved,
  onSaveError,
  intervention: controlledIntervention,
  interventionSelection,
  onInterventionChange,
  initialText = '',
  onTextChange,
  ...writingAssistProps
}) => {
  const [draft, setDraft] = useState(initialText);
  const [localIntervention, setLocalIntervention] = useState<WritingAssistGateEnvelope | null>(null);
  const [lastSelection, setLastSelection] = useState<WritingAssistSaveSelection | null>(null);
  const [writerResult, setWriterResult] = useState<WritingAssistSuccessResponse | null>(null);
  const [savedAssistReference, setSavedAssistReference] = useState<string | undefined>();
  const [writerBlock, setWriterBlock] = useState<{ category: BlockingCategory; excerpt: string } | null>(null);
  const [localSaveError, setLocalSaveError] = useState<ApiError | null>(null);
  const [editorKey, setEditorKey] = useState(0);
  const [autoPolishRequest, setAutoPolishRequest] = useState(0);
  const retryInFlight = useRef(false);

  const intervention = controlledIntervention === undefined
    ? localIntervention
    : controlledIntervention;
  const activeSelection = interventionSelection || lastSelection;

  useEffect(() => {
    setDraft(initialText);
    setLocalIntervention(null);
    setLastSelection(null);
    setWriterResult(null);
    setSavedAssistReference(undefined);
    setWriterBlock(null);
    setLocalSaveError(null);
    retryInFlight.current = false;
  }, [initialText, writingAssistProps.context]);

  const setIntervention = (next: WritingAssistGateEnvelope | null) => {
    setLocalIntervention(next);
    onInterventionChange?.(next);
  };

  const runSave = async (selection: WritingAssistSaveSelection) => {
    if (retryInFlight.current) return undefined;
    retryInFlight.current = true;
    setLastSelection(selection);
    setLocalSaveError(null);
    try {
      const result = await save(selection);
      setIntervention(null);
      setSavedAssistReference(writerResult?.assistReference);
      onSaved?.({ selection, result, writerResult });
      return result;
    } catch (error) {
      const envelope = writingAssistGateEnvelopeFromError(error);
      if (envelope) setIntervention(envelope);
      else if (isWritingAssistGateUnavailableError(error)) {
        // WA-L-06 is intentionally silent. Keep the exact draft and return to
        // the enabled editor so an explicit fail-closed outage is retry-safe.
      }
      else if (onSaveError) onSaveError(error);
      else {
        setLocalSaveError(error instanceof ApiError
          ? error
          : new ApiError(
            0,
            error instanceof Error && error.message
              ? error.message
              : "We couldn't save your changes. Please try again.",
          ));
      }
      return undefined;
    } finally {
      retryInFlight.current = false;
    }
  };

  const editSelection = (text: string, polish = false) => {
    setDraft(text);
    setIntervention(null);
    setWriterBlock(null);
    setEditorKey(current => current + 1);
    if (polish) setAutoPolishRequest(current => current + 1);
  };

  if (writerBlock) {
    return (
      <HardStop
        context={writingAssistProps.context}
        category={writerBlock.category}
        excerpt={writerBlock.excerpt}
        onEdit={() => editSelection(draft)}
      />
    );
  }

  if (intervention && activeSelection) {
    if (intervention.reason === 'coach') {
      return (
        <CoachInterrupt
          context={writingAssistProps.context}
          observations={intervention.coach.observations}
          firstCoach
          onPolish={() => editSelection(activeSelection.text, true)}
          onSaveOriginal={() => { void runSave(activeSelection); }}
        />
      );
    }
    if (intervention.reason === 'redaction_preview') {
      const violations = intervention.violations.filter(violation => (
        CONTACT_CATEGORIES.indexOf(violation.category as ContactViolationCategory) !== -1
      )) as Array<{ category: ContactViolationCategory }>;
      return (
        <RedactionPreview
          context={writingAssistProps.context}
          redactedText={intervention.redactedText || ''}
          violations={violations}
          onSaveRedacted={text => {
            void runSave({ ...activeSelection, text, hash: undefined });
          }}
          onEdit={() => editSelection(activeSelection.text)}
        />
      );
    }
    const blocking = intervention.violations.find(violation => (
      BLOCKING_CATEGORIES.indexOf(violation.category as BlockingCategory) !== -1
    ));
    if (blocking) {
      return (
        <HardStop
          context={writingAssistProps.context}
          category={blocking.category as BlockingCategory}
          excerpt={blocking.excerpt}
          onEdit={() => editSelection(activeSelection.text)}
        />
      );
    }
  }

  return (
    <>
      <WritingAssist
        key={editorKey}
        {...writingAssistProps as WritingAssistProps}
        initialText={draft}
        autoPolishRequest={autoPolishRequest}
        onTextChange={text => {
          setDraft(text);
          onTextChange?.(text);
        }}
        onWriterResult={(response: WritingAssistResponse) => {
          if ('refusal' in response) {
            const category = response.refusal.category;
            if (BLOCKING_CATEGORIES.indexOf(category as BlockingCategory) !== -1) {
              setWriterBlock({ category: category as BlockingCategory, excerpt: draft.slice(0, 240) });
            }
            return;
          }
          setWriterResult(response);
        }}
        onSave={runSave}
      />
      {savedAssistReference ? (
        <AssistFeedback
          assistReference={savedAssistReference}
          submitFeedback={request => Api.submitWritingAssistFeedback(request)}
        />
      ) : null}
      <FixedError error={localSaveError} onDismiss={() => setLocalSaveError(null)} />
    </>
  );
};

export default WritingAssistFlow;
